import { useMemo, Fragment } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getGapBetweenTimes } from '@components/Timeline/Timeline/utils';
import { useDriverSchedule } from '@features/drivers/hooks/useDriverSchedule';
import { processDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';
import { calculateUnavailableBlocks, getUnavailableBlockLabel } from '@hopdrive/sdk-appointments';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DriverScheduleOverlayProps {
  driverId: string | undefined;
  selectedDate: string; // YYYY-MM-DD format
  timezone: string;
  zoom?: number; // Pixels per minute (default: 4)
}

/**
 * Overlay component that displays unavailable time blocks for a driver
 * Shows gray diagonal stripes during times when the driver is off-shift,
 * on holiday/PTO, or not scheduled.
 */
const DriverScheduleOverlay = ({
  driverId,
  selectedDate,
  timezone,
  zoom = 4,
}: DriverScheduleOverlayProps) => {
  const theme = useTheme();

  // Fetch driver schedule from database
  const { driverSchedule, loading } = useDriverSchedule(driverId);

  // Process schedule data (merge with defaults, group by day)
  const processedSchedule = useMemo(() => {
    if (!driverId || !driverSchedule || driverSchedule.length === 0) {
      return undefined;
    }

    return processDriverSchedule(driverSchedule as any[], driverId);
  }, [driverSchedule, driverId]);

  // Calculate unavailable time blocks for the selected date
  const unavailableBlocks = useMemo(() => {
    if (loading) return [];

    return calculateUnavailableBlocks(
      processedSchedule,
      selectedDate,
      timezone
    );
  }, [processedSchedule, selectedDate, timezone, loading]);

  // Get start of day for positioning calculations
  // Note: .add(12, 'hours') is a workaround for timezone handling to ensure the correct day is selected
  // This must match the calculation in ScheduleAreaRow.tsx for proper block positioning
  const startOfDay = useMemo(() => {
    return dayjs(selectedDate).add(12, 'hours').tz(timezone).startOf('day').toISOString();
  }, [selectedDate, timezone]);

  // Don't render if no unavailable blocks
  if (unavailableBlocks.length === 0) {
    return null;
  }

  return (
    <>
      {unavailableBlocks.map((block, index) => {
        const leftPosition = getGapBetweenTimes(startOfDay, block.startTime, zoom);
        const width = getGapBetweenTimes(block.startTime, block.endTime, zoom);

        // Validation: Skip blocks with invalid positions
        if (leftPosition < 0 || width <= 0) {
          return null;
        }

        return (
          <Fragment key={`unavailable-${index}`}>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    {getUnavailableBlockLabel(block.reason)}
                  </Typography>
                </Box>
              }
              placement="top"
              followCursor
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: `${leftPosition}px`,
                  width: `${width}px`,
                  top: 0,
                  height: '100%',
                  background: `repeating-linear-gradient(
                    45deg,
                    ${theme.palette.grey[400]}40,
                    ${theme.palette.grey[400]}40 10px,
                    ${theme.palette.grey[500]}40 10px,
                    ${theme.palette.grey[500]}40 20px
                  )`,
                  pointerEvents: 'none',
                  zIndex: 1, // Behind all other timeline elements
                }}
              />
            </Tooltip>
          </Fragment>
        );
      })}
    </>
  );
};

export default DriverScheduleOverlay;
